import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import { useLivePrice } from '@/api/digital-gold/livePrice';
import { UseChangeSipStatus } from '@/api/digital-gold/sip/changeSipStatus';
import { useDgUserWallet } from '@/api/digital-gold/userWallet';
import AutoDebitIcon from '@/assets/icons/auto-debit.svg';
import CloseIcon from '@/assets/icons/close-icon.svg';
import PauseSIPIcon from '@/assets/icons/pause-sip.svg';
import ResumeSIPIcon from '@/assets/icons/resume-sip.svg';
import LeftArrowIcon from '@/assets/images/icons/black-arrow-left.svg';
import GoldIcon from '@/assets/images/icons/gold-coin-icon.svg';
import HelpIcon from '@/assets/images/icons/help-icon.svg';
import RupeeIcon from '@/assets/images/icons/rupee-icon.svg';
import SuccessIcon from '@/assets/images/icons/success-icon.svg';
import { useNotificationStore } from '@/common';
import { cookiesStorage } from '@/library/storage';
import type { ManageSIPSummaryPropType } from '@/types/manageSip';
import { authConstants } from '@/utils/constants';
import {
  getCycleName,
  getDayByDate,
  getFormattedDate,
  getSipStatusIcon,
  ManageSIPConstants,
  SIPActionTypes,
  SipEnums,
  sipStatusEnums,
} from '@/utils/functions/SIPs';

export const ManageSIPSummary = (props: ManageSIPSummaryPropType) => {
  const { SIPSummary, setActivePage } = props;

  const ChangeSIPStatus = UseChangeSipStatus();
  const { addNotification } = useNotificationStore();
  const customerId = cookiesStorage.getCookie(authConstants.OM_AUTH_USER_ID);

  const onClickChangeSIPStatus = async (
    sipActionType: string,
    currentSipStatus: string
  ) => {
    const reqBody = {
      customerId,
      SipActionType: sipActionType,
      payload: {
        pgSubscriptionId: SIPSummary.pgSubscriptionId,
        status: currentSipStatus,
      },
    };
    const res = await ChangeSIPStatus.mutateAsync(reqBody);
    if (res.status === 200) {
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'SIP Status updated successfully',
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Something went wrong, Unable to update the status',
      });
    }
  };

  const { resultData } = useDgUserWallet({
    customerId: `${customerId}`,
  });

  const { livePriceData } = useLivePrice();
  const livePriceAmount: number = livePriceData.saleGoldRate;

  //  pause & close - active cases
  //  resume & close - paused cases
  //  none - closed

  return (
    <div>
      <main className="home pledge loanDetails buyGold manageSip repayment bg bg--light">
        <section>
          <div className="container">
            <div className="headerCard">
              <p className="text--black text--bold title">
                <button
                  onClick={() => {
                    setActivePage(ManageSIPConstants.manageSip);
                  }}
                >
                  <Image
                    style={{ color: undefined }}
                    src={LeftArrowIcon}
                    alt=""
                    // style={{ marginRight: '12px' }}
                    className="mr-3"
                  />
                </button>
                {getCycleName(SIPSummary.paymentCycle)} SIP{' '}
              </p>
            </div>
            <div className="row">
              <div className="col-lg-7">
                <div
                  className="loanDetails__card loanCard heading-withIcon d-flex items-start"
                  // style={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <Image
                    style={{ color: undefined }}
                    className="img-fluid mr-2"
                    // style={{ marginRight: '8px' }}
                    src={getSipStatusIcon(SIPSummary.paymentCycle)}
                    alt="sip status icon"
                  />
                  <div className="right">
                    <p className="loanCard__title text--bold text--black with-desc">
                      {/* SIP is {SIPSummary.sipStatus} */}
                      SIP is {sipStatusEnums[SIPSummary.sipStatus]}
                    </p>
                    <p className="loanCard__desc text--regular text--gray mb-0">
                      Last debit was made on{' '}
                      {getFormattedDate(SIPSummary.lastDeductionDate)}
                    </p>
                  </div>
                </div>
                <div className="loanDetails__card loanCard">
                  <div className="loanRequirements">
                    <p className="loanCard__title text--bold text--black">
                      SIP Setting
                    </p>
                    <div className="payable">
                      <p className="text--black text--regular desc">
                        <span className="extraBig">SIP Amount</span>
                      </p>
                      <p className="text--black text--bold price extraBig">
                        ₹{SIPSummary.amount}
                      </p>
                    </div>
                    <div className="payable final">
                      <p className="text--gray text--regular desc small">
                        Frequency
                      </p>
                      <p className="text--gray text--semi price small">
                        {getCycleName(SIPSummary.paymentCycle)} on{' '}
                        {getDayByDate(
                          SIPSummary.startDate,
                          SIPSummary.paymentCycle
                        )}
                      </p>
                    </div>
                    <div className="payable">
                      <p className="text--gray text--regular desc">
                        <span className="small">Start Date </span>
                      </p>
                      <p className="text--gray text--semi price">
                        {getFormattedDate(SIPSummary.startDate)}
                      </p>
                    </div>
                    <div className="payable">
                      <p className="text--gray text--regular desc">
                        <span className="small">End Date</span>
                      </p>
                      <p className="text--gray text--semi price">
                        {getFormattedDate(SIPSummary.endDate)}
                      </p>
                    </div>
                  </div>
                </div>
                {/* <div className="loanDetails__card loanCard">
                  <p className="loanCard__title text--bold text--black">
                    Payment Method
                  </p>
                  <div className="paymentDetail-info mb-0">
                    <div className="detail">
                      <Image 
style={{ color: undefined }}
                        className="img-fluid"
                        src="./assets/images/icons/upi-icon.png"
                        alt="UPI logo"
                      />
                      <div className="right">
                        <p className="text--black text--bold title">
                          username@bankname.com
                        </p>
                        <p className="text--gray text--regular desc">
                          UPI transfer
                        </p>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>
              <div className="col-lg-5">
                <div className="loanDetails__card loanCard tablet-margin">
                  <div className="loanCard__title-withChips">
                    <p className="loanCard__title text--bold text--black mb-0">
                      My Gold Balance
                    </p>
                    {/* <div className="chips__container">
                      <p className="chips highChips">
                        <Image 
style={{ color: undefined }}
                          className="img-fluid"
                          src="./assets/images/icons/trend-up.svg"
                          alt="trend up"
                        />
                        + ₹3.04 (2.45%)
                      </p>
                    </div> */}
                  </div>
                  <div className="loanCard__dates verticle-card">
                    <div className="left">
                      <span className="text--regular text--gray title">
                        <Image
                          style={{ color: undefined }}
                          className="img-fluid mobile-icon"
                          src={GoldIcon}
                          alt="gold coin"
                        />{' '}
                        In Grams
                      </span>
                      <span className="text--bold text--black price">
                        <Image
                          style={{ color: undefined }}
                          className="img-fluid web-icon"
                          src={GoldIcon}
                          alt="gold coin"
                        />{' '}
                        {/* 57.89 gms */}
                        {resultData !== undefined
                          ? (
                              Number(resultData?.goldBalance) +
                              Number(resultData?.nonInvoicedBalance)
                            ).toFixed(4)
                          : 0.0}{' '}
                        gms
                      </span>
                    </div>
                    <div className="right">
                      <span className="text--regular text--gray title">
                        <Image
                          style={{ color: undefined }}
                          className="img-fluid mobile-icon"
                          src={RupeeIcon}
                          alt="rupee icon"
                        />{' '}
                        Current Value
                      </span>
                      <span className="text--bold text--black price">
                        <Image
                          style={{ color: undefined }}
                          className="img-fluid web-icon"
                          src={RupeeIcon}
                          alt="rupee icon"
                        />{' '}
                        {/* ₹0.00 */}₹
                        {resultData !== undefined
                          ? (
                              (parseFloat(resultData?.goldBalance.toString()) +
                                parseFloat(
                                  resultData?.nonInvoicedBalance.toString()
                                )) *
                              livePriceAmount
                            ).toFixed(2)
                          : 0.0}
                      </span>
                    </div>
                  </div>
                </div>
                <a
                  className="loanDetails__card loanCard actionCard tablet-margin autoDebit"
                  onClick={() => {
                    setActivePage(ManageSIPConstants.manageSip);
                  }}
                >
                  <div className="left">
                    <Image
                      style={{ color: undefined }}
                      className="img-fluid"
                      src={AutoDebitIcon}
                      alt="auto debit icon"
                    />
                    <p className="loanCard__title text--bold text--black mb-0">
                      Auto debit history
                    </p>
                  </div>
                  <div className="right">
                    <span className="circleArrow-icon" />
                  </div>
                </a>
                {/* Pause SIP CTA */}
                {SIPSummary.sipStatus === SipEnums.Active && (
                  <a
                    // href="javascript:void(0)"
                    className="loanDetails__card loanCard actionCard"
                    data-toggle="modal"
                    data-target="#pauseSIPModal"
                  >
                    <div className="left">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={PauseSIPIcon}
                        alt="pause sip icon"
                      />
                      <p className="loanCard__title text--bold text--black mb-0">
                        Pause SIP
                      </p>
                    </div>
                    <div className="right">
                      <span className="circleArrow-icon" />
                    </div>
                  </a>
                )}
                {/* Resume SIP */}
                {SIPSummary.sipStatus === SipEnums.Paused && (
                  <a
                    // href="javascript:void(0)"
                    className="loanDetails__card loanCard actionCard"
                    data-toggle="modal"
                    data-target="#sipActivatedModal"
                    onClick={() =>
                      onClickChangeSIPStatus(
                        SIPActionTypes.sipResume,
                        SipEnums.Active
                      )
                    }
                  >
                    <div className="left">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={ResumeSIPIcon}
                        alt="resume sip icon"
                      />
                      <p className="loanCard__title text--bold text--black mb-0">
                        Resume SIP
                      </p>
                    </div>
                    <div className="right">
                      <span className="circleArrow-icon" />
                    </div>
                  </a>
                )}
                {/* Close SIP */}
                {(SIPSummary.sipStatus === SipEnums.Active ||
                  SIPSummary.sipStatus === SipEnums.Paused) && (
                  <a
                    // href="javascript:void(0)"
                    className="loanDetails__card loanCard actionCard"
                    data-toggle="modal"
                    data-target="#closeSIPModal"
                  >
                    <div className="left">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={CloseIcon}
                        alt="close sip icon"
                      />
                      <p className="loanCard__title text--bold text--black mb-0">
                        Close SIP
                      </p>
                    </div>
                    <div className="right">
                      <span className="circleArrow-icon" />
                    </div>
                  </a>
                )}
                <Link
                  href="/help"
                  className="loanDetails__card loanCard actionCard"
                  id="DG_manage-sip_help"
                >
                  <div className="left">
                    <Image
                      style={{ color: undefined }}
                      className="img-fluid"
                      src={HelpIcon}
                      alt="help icon"
                    />
                    <p className="loanCard__title text--bold text--black mb-0">
                      Help &amp; Support
                    </p>
                  </div>
                  <div className="right">
                    <span className="circleArrow-icon" />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div>
        {/* Close SIP popup */}
        <div
          className="modal loanDetails__modal repayModal visitSchedule pledge firstModal otpModal kycConfirmModal fade"
          id="closeSIPModal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="closeSIPModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="header border-0 p-0">
                <div className="modal__title mb-0">
                  <div className="left">
                    <p className="text--semi text--black label">
                      Are you sure you want to close SIP?
                    </p>
                    <p className="text--regular text--gray desc">
                      If you disable today, all future debits will not be
                      processed
                    </p>
                  </div>
                  <a
                    // href="javascript:void(0)"
                    data-dismiss="modal"
                  >
                    <Image
                      style={{ color: undefined }}
                      className="img-fluid"
                      src={CloseIcon}
                      alt="close icon"
                    />
                  </a>
                </div>
              </div>
              <div
                className="action-container d-flex mt-0"
                // style={{ display: 'flex' }}
              >
                <a
                  // href="javascript:void(0)"
                  className="custom__button custom__button--dark border-btn backBtn text--bold manage_width"
                  data-dismiss="modal"
                  // style={{ minWidth: 'auto' }}
                >
                  Cancel
                </a>
                <a
                  // href="javascript:void(0)"
                  className="custom__button custom__button--dark text--semi manage-sip-popup-confirm-cta"
                  data-dismiss="modal"
                  onClick={() =>
                    onClickChangeSIPStatus(
                      SIPActionTypes.sipCancel,
                      SipEnums.Cancelled
                    )
                  }
                >
                  Close SIP
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* activate SIP modal */}
        <div
          className="modal addressDetail__modal raiseSuccess successModal secondModal fade"
          id="sipActivatedModal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="sipActivatedModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="successMessage mb-0">
                <Image
                  style={{ color: undefined }}
                  className="img-fluid"
                  src={SuccessIcon}
                  alt="success icon"
                />
                <p className="text--black text--semi title mb-0">
                  Your SIP is Activated
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* pause SIP Modal */}
        <div
          className="modal loanDetails__modal repayModal visitSchedule pledge firstModal otpModal kycConfirmModal fade"
          id="pauseSIPModal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="pauseSIPModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="header border-0 p-0">
                <div className="modal__title mb-0">
                  <div className="left">
                    <p className="text--semi text--black label">
                      Are you sure you want to pause SIP?
                    </p>
                    <p className="text--regular text--gray desc">
                      If you disable today, all future debits will not be
                      processed
                    </p>
                  </div>
                  <a
                    // href="javascript:void(0)"
                    data-dismiss="modal"
                  >
                    <Image
                      style={{ color: undefined }}
                      className="img-fluid"
                      src={CloseIcon}
                      alt="close icon"
                    />
                  </a>
                </div>
              </div>
              <div
                className="action-container d-flex mt-0"
                // style={{ display: 'flex' }}
              >
                <a
                  // href="javascript:void(0)"
                  className="custom__button custom__button--dark border-btn backBtn text--bold manage_width"
                  data-dismiss="modal"
                  // style={{ minWidth: 'auto' }}
                >
                  Cancel
                </a>
                <a
                  // href="javascript:void(0)"
                  className="custom__button custom__button--dark text--semi manage-sip-popup-confirm-cta"
                  data-dismiss="modal"
                  onClick={() =>
                    onClickChangeSIPStatus(
                      SIPActionTypes.sipPause,
                      SipEnums.Paused
                    )
                  }
                >
                  Pause SIP
                </a>
              </div>
            </div>
          </div>
          {/* <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="header p-0 border-0">
                <div className="modal__title mb-0">
                  <div className="left">
                    <p className="text--semi text--black mb-0 label">
                      Pause SIP
                    </p>
                  </div>
                  <a 
                  // href="javascript:void(0)" 
                  data-dismiss="modal">
                    <Image 
style={{ color: undefined }}
                      className="img-fluid"
                      src={CloseIcon}
                      alt="close icon"
                    />
                  </a>
                </div>
                <div className="pause-radio-container">
                  <div className="item">
                    <label className="radio__circle">
                      <input type="radio" name="pauseSip" />
                      <span className="radio__circle-checkmark" />
                      <p className="text--regular text--black radio__circle-label">
                        Till end of this week
                      </p>
                    </label>
                    <p className="text--regular text--gray title">
                      Will pause until{" "}
                      <span className="text--black text--semi">
                        16th Oct 2022
                      </span>{" "}
                      &amp; then the SIP will restart from 18th Oct 2022
                    </p>
                  </div>
                  <div className="item">
                    <label className="radio__circle">
                      <input type="radio" name="pauseSip" />
                      <span className="radio__circle-checkmark" />
                      <p className="text--regular text--black radio__circle-label">
                        Till end of this month
                      </p>
                    </label>
                    <p className="text--regular text--gray title">
                      Will pause until{" "}
                      <span className="text--black text--semi">
                        16th Oct 2022
                      </span>{" "}
                      &amp; then the SIP will restart from 18th Oct 2022
                    </p>
                  </div>
                  <div className="item">
                    <label className="radio__circle">
                      <input type="radio" name="pauseSip" />
                      <span className="radio__circle-checkmark" />
                      <p className="text--regular text--black radio__circle-label">
                        Till end of next month
                      </p>
                    </label>
                    <p className="text--regular text--gray title">
                      Will pause until{" "}
                      <span className="text--black text--semi">
                        16th Oct 2022
                      </span>{" "}
                      &amp; then the SIP will restart from 18th Oct 2022
                    </p>
                  </div>
                  <div className="item datepickerContainer">
                    <label className="radio__circle">
                      <input type="radio" name="pauseSip" />
                      <span className="radio__circle-checkmark" />
                      <p className="text--regular text--black radio__circle-label">
                        Custom
                      </p>
                    </label>
                    <div className="input-form input-calendar">
                      <input type="text" placeholder="DD/MM/YYYY" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="action-container mt-0">
                <a
                  // href="javascript:void(0)"
                  className="custom__button custom__button--dark text--semi w-100"
                  data-dismiss="modal"
                >
                  Pause SIP
                </a>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};
